import React, { useCallback, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import * as yup from 'yup';

import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import { invoiceCreateSchema } from '~/components/InvoiceTools/schema/invoice.schema';
import {
  extractDocumentIdFromS3Uri,
  generateInvoice,
  getLineItemsFromOrder,
  getPaymentsForOrder,
  getSignedInvoiceDownloadLink,
} from '~/components/InvoiceTools/tabs/invoiceGeneratorUtils';

import { useCreateOrderDocument } from '~/hooks/apis/order-documents/useCreateOrderDocument';

import { getBusiness } from '~/services/BusinessTraveller/BusinessTravellerService';
import { getOrderDocuments } from '~/services/OrderDocumentService';
import { getOrderDetail } from '~/services/OrdersService';

import LineItemField from '../components/LineItemField';
import PaymentField from '../components/PaymentField';

const CURRENCY_OPTIONS = ['AUD', 'NZD', 'USD', 'SGD', 'EUR', 'GBP'];

type InvoiceCreate = yup.InferType<typeof invoiceCreateSchema>;

const DEFAULT_LINE_ITEM = {
  description: '',
  bookingNumber: '',
  totalPrice: 0,
  gstAmount: 0,
};

const DEFAULT_PAYMENT = {
  description: '',
  amount: 0,
  date: new Date(),
};

const DEFAULT_VALUES: InvoiceCreate = {
  invoiceNumber: '',
  orderId: '',
  orderNumber: '',
  brand: 'LEBT',
  currency: 'AUD',
  date: new Date(),
  customer: {
    name: '',
    email: '',
    businessName: '',
    abn: '',
  },
  lineItems: [{ ...DEFAULT_LINE_ITEM }],
  payments: [{ ...DEFAULT_PAYMENT }],
};

function downloadFromLink(uri: string) {
  const link = document.createElement('a');
  link.href = uri;
  link.download = '';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function InvoiceGeneratorTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [lookupOrderId, setLookupOrderId] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [generatedInvoiceS3Path, setGeneratedInvoiceS3Path] = useState<string | null>(null);
  const [order, setOrder] = useState<App.Order | null>(null);

  const generateInvoiceMutation = useMutation({
    mutationFn: generateInvoice,
    onSuccess: (response) => {
      setGeneratedInvoiceS3Path(response.s3Path);
      enqueueSnackbar('Invoice generated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      setGeneratedInvoiceS3Path(null);
      setOrder(null);
      enqueueSnackbar(`Failed to generate invoice: ${error.message}`, { variant: 'error' });
    },
  });

  const downloadInvoiceMutation = useMutation({
    mutationFn: getSignedInvoiceDownloadLink,
    onSuccess: (signedUrl) => {
      downloadFromLink(signedUrl);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to download invoice: ${error.message}`, { variant: 'error' });
    },
  });

  const createOrderDocumentMutation = useCreateOrderDocument({
    onSuccess: () => {
      enqueueSnackbar('Document saved to order successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to save document to order: ${error.message}`, { variant: 'error' });
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<InvoiceCreate>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(invoiceCreateSchema),
  });

  const isSubmitting = generateInvoiceMutation.isPending;

  const {
    fields: lineItemFields,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const {
    fields: paymentFields,
    append: appendPayment,
    remove: removePayment,
  } = useFieldArray({
    control,
    name: 'payments',
  });

  const handleAddLineItem = useCallback(() => {
    appendLineItem({ ...DEFAULT_LINE_ITEM });
  }, [appendLineItem]);

  const handleAddPayment = useCallback(() => {
    appendPayment({ ...DEFAULT_PAYMENT, date: new Date() });
  }, [appendPayment]);

  const handleLookupOrder = useCallback(async () => {
    if (!lookupOrderId.trim()) {
      setLookupError('Please enter an order ID');
      return;
    }

    setIsLookingUp(true);
    setLookupError(null);
    setGeneratedInvoiceS3Path(null);
    setOrder(null);

    try {
      const order = await getOrderDetail(lookupOrderId.trim());

      if (!order) {
        setLookupError('Order not found');
        return;
      }

      // Fetch business details if business_id exists
      let business: App.Business | null = null;
      if (order.business_id) {
        try {
          business = await getBusiness(order.business_id);
        } catch {
          // Business fetch failed, continue without business details
        }
      }

      // Build line items from order items (primarily flights for LEBT)
      const lineItems: InvoiceCreate['lineItems'] = getLineItemsFromOrder(order);

      // If no line items found, add a default one
      if (lineItems.length === 0) {
        lineItems.push({ ...DEFAULT_LINE_ITEM });
      }

      // Build payments from order payments
      const payments: InvoiceCreate['payments'] = getPaymentsForOrder(order);

      // If no payments found, add a default one
      if (payments.length === 0) {
        payments.push({ ...DEFAULT_PAYMENT });
      }

      // Fetch all order documents (including rescinded) to determine invoice number
      const orderDocuments = await getOrderDocuments(order.id_orders || order.id);
      const numberOfExistingInvoices = orderDocuments?.filter((doc) => doc.document_type === 'INVOICE').length || 0;
      const invoiceNumber = `LEBT-${order.number}-${numberOfExistingInvoices + 1}`;

      // Reset form with order data
      reset({
        invoiceNumber,
        orderId: order.id_orders || order.id,
        orderNumber: order.number,
        brand: 'LEBT',
        currency: order.currency_code || 'AUD',
        date: new Date(),
        customer: {
          name: order.customer_full_name || '',
          email: order.customer_email || '',
          businessName: business?.name || '',
          abn: business?.businessIdentifier || '',
        },
        lineItems,
        payments,
      });

      // Setting the order for usage while generating the SQL
      setOrder(order);

      enqueueSnackbar('Order loaded successfully', { variant: 'success' });
    } catch (err) {
      const error = err as Error;
      setLookupError(error.message || 'Failed to load order');
    } finally {
      setIsLookingUp(false);
    }
  }, [lookupOrderId, reset, enqueueSnackbar]);

  const onSubmit: SubmitHandler<InvoiceCreate> = useCallback(
    (formData) => {
      generateInvoiceMutation.mutate(formData);
    },
    [generateInvoiceMutation],
  );

  const handleInvoiceDownload = useCallback(
    (s3Uri: string) => {
      if (!s3Uri?.trim()) {
        enqueueSnackbar('Missing invoice S3 URI', { variant: 'error' });
        return;
      }
      downloadInvoiceMutation.mutate(s3Uri);
    },
    [downloadInvoiceMutation, enqueueSnackbar],
  );

  const handleSaveDocumentToOrder = useCallback(() => {
    if (!order || !generatedInvoiceS3Path) {
      return;
    }

    const orderItems = getLineItemsFromOrder(order)
      .filter((item) => item.type !== 'payment_fee')
      .map((item) => ({
        item_type: item.type,
        item_id: item.id,
      }));

    createOrderDocumentMutation.mutate({
      orderId: order.id_orders,
      payload: {
        order_items: orderItems,
        document_id: extractDocumentIdFromS3Uri(generatedInvoiceS3Path),
        document_type: 'INVOICE',
        s3_object_uri: generatedInvoiceS3Path,
      },
    });
  }, [order, generatedInvoiceS3Path, createOrderDocumentMutation]);

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Use this tool to generate custom invoices for business traveller orders.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Load from Order
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter an order ID to pre-populate the form with order details.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            label="Order ID"
            value={lookupOrderId}
            onChange={(e) => setLookupOrderId(e.target.value)}
            placeholder="e.g., bfb7561d-77e9-4c3f-861d-06d0736ac1ab"
            fullWidth
            disabled={isLookingUp}
            error={!!lookupError}
            helperText={lookupError}
            sx={{ maxWidth: 500 }}
          />
          <Button
            variant="contained"
            onClick={handleLookupOrder}
            disabled={isLookingUp || !lookupOrderId.trim()}
            startIcon={isLookingUp ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
          >
            {isLookingUp ? 'Loading...' : 'Load Order'}
          </Button>
          {lookupOrderId.trim() && (
            <Link
              component={RouterLink}
              to={`/purchases/${lookupOrderId.trim()}`}
              target="_blank"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              View Order
              <OpenInNewIcon fontSize="small" />
            </Link>
          )}
        </Stack>
      </Paper>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Details
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  {...register('invoiceNumber')}
                  label="Invoice Number"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  {...register('orderId')}
                  label="Order ID"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.orderId}
                  helperText={errors.orderId?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  {...register('orderNumber')}
                  label="Order Number"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.orderNumber}
                  helperText={errors.orderNumber?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Controller
                  name="brand"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Brand"
                      select
                      fullWidth
                      required
                      disabled={isSubmitting}
                      error={!!errors.brand}
                      helperText={errors.brand?.message}
                      slotProps={{ inputLabel: { shrink: true } }}
                    >
                      <MenuItem value="LEBT">LEBT</MenuItem>
                    </TextField>
                  )}
                />
                <TextField
                  {...register('currency')}
                  label="Currency"
                  select
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.currency}
                  helperText={errors.currency?.message}
                  defaultValue="AUD"
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {CURRENCY_OPTIONS.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DateTimeWidget
                      value={field.value instanceof Date ? field.value.toISOString() : String(field.value)}
                      onChange={(value) => field.onChange(new Date(value))}
                      label="Invoice Date"
                      disabled={isSubmitting}
                      required
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Details
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  {...register('customer.name')}
                  label="Customer Name"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.customer?.name}
                  helperText={errors.customer?.name?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  {...register('customer.email')}
                  label="Customer Email"
                  type="email"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.customer?.email}
                  helperText={errors.customer?.email?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  {...register('customer.businessName')}
                  label="Business Name"
                  fullWidth
                  disabled={isSubmitting}
                  error={!!errors.customer?.businessName}
                  helperText={errors.customer?.businessName?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  {...register('customer.abn')}
                  label="ABN"
                  fullWidth
                  disabled={isSubmitting}
                  error={!!errors.customer?.abn}
                  helperText={errors.customer?.abn?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Line Items</Typography>
              <Button variant="text" startIcon={<AddIcon />} onClick={handleAddLineItem} disabled={isSubmitting}>
                Add Line Item
              </Button>
            </Stack>
            {errors.lineItems?.message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.lineItems.message}
              </Alert>
            )}
            {lineItemFields.map((field, index) => (
              <LineItemField
                key={field.id}
                index={index}
                register={register}
                errors={errors}
                onRemove={() => removeLineItem(index)}
                canRemove={lineItemFields.length > 1}
                disabled={isSubmitting}
              />
            ))}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Payments</Typography>
              <Button variant="text" startIcon={<AddIcon />} onClick={handleAddPayment} disabled={isSubmitting}>
                Add Payment
              </Button>
            </Stack>
            {errors.payments?.message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.payments.message}
              </Alert>
            )}
            {paymentFields.map((field, index) => (
              <PaymentField
                key={field.id}
                index={index}
                register={register}
                errors={errors}
                control={control}
                onRemove={() => removePayment(index)}
                canRemove={paymentFields.length > 1}
                disabled={isSubmitting}
              />
            ))}
          </Paper>

          <Divider />

          <Stack direction="row" spacing={2}>
            <Button
              type="submit"
              variant="contained"
              disabled={generateInvoiceMutation.isPending}
              startIcon={generateInvoiceMutation.isPending ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {generateInvoiceMutation.isPending ? 'Generating...' : 'Generate Invoice'}
            </Button>
          </Stack>

          {generatedInvoiceS3Path && order && (
            <Paper sx={{ p: 3, bgcolor: '#e8f5e9' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Invoice generated successfully.
              </Alert>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={
                    downloadInvoiceMutation.isPending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() => handleInvoiceDownload(generatedInvoiceS3Path)}
                  disabled={downloadInvoiceMutation.isPending}
                >
                  {downloadInvoiceMutation.isPending ? 'Downloading...' : 'Download Invoice'}
                </Button>

                <Button
                  variant="contained"
                  startIcon={
                    createOrderDocumentMutation.isPending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveDocumentToOrder}
                  disabled={createOrderDocumentMutation.isPending}
                >
                  {createOrderDocumentMutation.isPending ? 'Saving...' : 'Save Document to Order'}
                </Button>
              </Stack>
            </Paper>
          )}
        </Stack>
      </form>
    </Box>
  );
}
